import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import dkLogo from '@/assets/damian-keyes-logo.png';
import { trackLead } from '@/lib/metaPixel';

const navItems = [
  { label: 'The Trap', href: '/#the-real-issue', sectionId: 'the-real-issue' },
  { label: 'The Framework', href: '/#framework', sectionId: 'framework' },
  { label: 'The Founder', href: '/#founder', sectionId: 'founder' },
  { label: 'The Proof', href: '/#artist-results', sectionId: 'artist-results' },
];

const applyUrl = "https://a23j5fry30t.typeform.com/to/CzjaFT5z";

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      // Already on homepage - just scroll
      const element = document.getElementById(sectionId);
      element?.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Navigate to homepage, then scroll after a brief delay
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        element?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Width animation trigger - when scrollY > 20px
  useEffect(() => {
    let ticking = false;
    
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 20);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Active section detection
  useEffect(() => {
    const detectActiveSection = () => {
      const sections = navItems.map(item => ({
        id: item.sectionId,
        element: document.getElementById(item.sectionId)
      })).filter(s => s.element);
      
      const viewportTop = window.scrollY + window.innerHeight * 0.4;
      
      // Clear active state when we reach "Next Steps" section
      const nextStepsSection = document.getElementById('next-steps');
      if (nextStepsSection && viewportTop > nextStepsSection.offsetTop) {
        setActiveSection(null);
        return;
      }
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.element && section.element.offsetTop <= viewportTop) {
          setActiveSection(section.id);
          return;
        }
      }
      setActiveSection(null);
    };

    window.addEventListener('scroll', detectActiveSection, { passive: true });
    detectActiveSection();
    return () => window.removeEventListener('scroll', detectActiveSection);
  }, []);

  // Theme detection using elementFromPoint
  useEffect(() => {
    let ticking = false;
    
    const detectTheme = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const navBottom = navRef.current?.getBoundingClientRect().bottom ?? 80;
          const centerX = window.innerWidth / 2;
          const element = document.elementFromPoint(centerX, navBottom + 10);
          
          // Traverse up DOM tree to find data-nav-theme attribute
          let current = element;
          while (current && current !== document.body) {
            const themeAttr = current.getAttribute('data-nav-theme');
            if (themeAttr === 'light' || themeAttr === 'dark') {
              setTheme(themeAttr);
              ticking = false;
              return;
            }
            current = current.parentElement;
          }
          
          // Default fallback
          setTheme('dark');
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', detectTheme, { passive: true });
    window.addEventListener('resize', detectTheme, { passive: true });
    detectTheme(); // Initial detection

    return () => {
      window.removeEventListener('scroll', detectTheme);
      window.removeEventListener('resize', detectTheme);
    };
  }, []);

  return (
    <nav 
      ref={navRef}
      className={`
        fixed top-0 left-0 right-0 z-50 pt-3 md:pt-6
        transition-all duration-300 ease-out
      `}
    >
      <div className={`${isScrolled ? 'max-w-7xl lg:max-w-5xl' : 'max-w-7xl'} mx-auto px-6 lg:px-12 transition-all duration-300 ease-out`}>
        <div 
          className={`
            w-full py-2 md:py-3 px-4 md:px-6 rounded-2xl
            flex items-center justify-between
            ${theme === 'light' 
              ? 'bg-neutral-50 border border-neutral-100' 
              : 'bg-neutral-950/90 border border-white/5'
            }
          `}
        >
        {/* Logo */}
        <Link to="/" className="h-8 md:h-10 block">
          <img 
            src={dkLogo} 
            alt="DK Music Group" 
            className={`
              h-8 md:h-10 w-auto object-contain 
              transition-all duration-300 cursor-pointer
              hover:scale-105
              ${theme === 'light' ? '' : 'brightness-0 invert'}
            `}
          />
        </Link>
        
        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-6">
          {navItems.map((item) => {
            const isActive = activeSection === item.sectionId;
            return (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.sectionId)}
                className={`
                  relative font-geist font-medium tracking-tight text-sm
                  transition-all duration-300 px-3 py-1.5 rounded-lg
                  ${isActive
                    ? theme === 'light'
                      ? 'bg-neutral-900/[0.08] text-neutral-900'
                      : 'bg-white/10 text-white'
                    : theme === 'light'
                      ? 'text-neutral-900 hover:text-neutral-600'
                      : 'text-white/90 hover:text-white'
                  }
                  ${!isActive ? `
                    after:content-[''] after:absolute after:bottom-0 after:left-3 after:right-3
                    after:h-[1px] after:origin-left
                    after:scale-x-0 after:transition-transform after:duration-250 after:ease-out
                    hover:after:scale-x-100
                    ${theme === 'light' ? 'after:bg-neutral-900' : 'after:bg-white'}
                  ` : ''}
                `}
              >
                {item.label}
              </a>
            );
          })}
          
          {/* Apple Blue Button */}
          <Button
            className="
              h-8 md:h-10 px-4 md:px-5 text-xs md:text-sm font-semibold rounded-xl
              bg-[#0071e3] hover:bg-[#0077ed] text-white
              transition-all duration-200
              hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,113,227,0.4)]
              active:translate-y-0 active:shadow-none
            "
            asChild
          >
            <a href={applyUrl} target="_blank" rel="noopener noreferrer" onClick={trackLead}>
              Apply
            </a>
          </Button>
        </div>
        
        {/* Mobile Apply Button */}
        <div className="md:hidden">
          <Button
            className="
              h-8 px-4 text-xs font-semibold rounded-xl
              bg-[#0071e3] hover:bg-[#0077ed] text-white
              transition-all duration-200
              hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(0,113,227,0.4)]
              active:translate-y-0 active:shadow-none
            "
            asChild
          >
            <a href={applyUrl} target="_blank" rel="noopener noreferrer" onClick={trackLead}>
              Apply
            </a>
          </Button>
        </div>
        </div>
      </div>
    </nav>
  );
}